"use client";
import { setUserAndContentCultureCookieAction } from "@/actions/user.actions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  CookiesEnum,
  cookiesValues,
  DialectsEnum,
  EventNamesEnum,
  LocalStorageKeysEnum,
  SearchStageEnum,
  URLQueryParamsEnum,
  UseTrackingAnalyticsSendToEnum,
} from "@/constants/constants";

import { useUserContext } from "@/context/UserProvider";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useCulture } from "@/hooks/useCulture";
import { useDialog } from "@/hooks/useDialog";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import { Culture } from "@/types/setting.types";
import {
  getClientSideCookieValue,
  getDefaultDialect,
} from "@/utils/common.helpers";
import { ChevronRight } from "lucide-react";
import { useParams, usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { Box } from "../Box";
import { Button } from "../Button";
import { SearchIcon } from "../Icons";
import { Img } from "../Img";
import { Logo } from "../Logo";
import { Column, Row } from "../Stack";
import { Text } from "../Text";
import { Input } from "../ui/input";
import { handlePostLoginRedirection } from "../login/utils/login.helpers";

export enum DialogType {
  MAIN_CULTURES = "main_cultures",
  OTHER_CULTURES = "other_cultures",
  CONTENT_CULTURE = "content_culture",
}

export const UserCulturePopup = ({
  initialDialogType = DialogType.MAIN_CULTURES,
  tempCulture,
  onlyForLoggedInUser = false,
  allowAutoSelectedFromURL,
}: {
  initialDialogType?: DialogType;
  tempCulture?: string;
  onlyForLoggedInUser?: boolean;
  allowAutoSelectedFromURL?: boolean;
}) => {
  const [isSkipPaywall, setIsSkipPaywall] = useState(false);
  const { t } = useClientTranslations();
  const { isLoggedIn } = useUserContext();
  const { availableCultures, enabledCultures, otherCultures } = useCulture();
  const params = useParams();
  const cultureFromParams = params.dialect;
  const { login } = useUserContext();

  const pathname = usePathname();

  const [selectedUserCulture, setSelectedUserCulture] = useState<
    string | number | null
  >(null);
  const [selectedContentCulture, setSelectedContentCulture] = useState<
    string | number | null
  >(null);
  const [currDialogType, setCurrDialogType] =
    useState<DialogType>(initialDialogType);
  const [selectCultureError, setSelectCultureError] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  const router = useRouter();
  const { isDialogOpen, closeDialog } = useDialog(true);
  const { trackEvent } = useTrackingAnalytics();

  const enabledCulturesShownPosition = useMemo(
    () =>
      enabledCultures
        .map(
          (culture, index) =>
            `${index + 1}_${culture.name.toLocaleLowerCase()}`,
        )
        .join("_"),
    [enabledCultures],
  );
  const otherCulturesShownPosition = useMemo(
    () =>
      otherCultures
        .map(
          (culture, index) =>
            `${index + 1}_${culture.name.toLocaleLowerCase()}`,
        )
        .join("_"),
    [otherCultures],
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const skipPaywall = searchParams.get(URLQueryParamsEnum.SKIP_PAYWALL);
    if (skipPaywall === "true") {
      setIsSkipPaywall(true);
    }
  }, []);

  useEffect(() => {
    setSelectCultureError(null);
  }, [selectedUserCulture, selectedContentCulture]);

  useEffect(() => {
    if (tempCulture) {
      const tempCultureObj = availableCultures.find((culture) =>
        [culture.name.toLocaleLowerCase(), culture.abbreviation].includes(
          tempCulture,
        ),
      );
      if (tempCultureObj) {
        setSelectedUserCulture(tempCultureObj.abbreviation);
      }
    } else if (cultureFromParams && allowAutoSelectedFromURL) {
      setSelectedUserCulture(
        ((cultureFromParams as string) || getDefaultDialect()).slice(0, 3),
      );
    }
  }, [tempCulture]);

  const openOtherCulturesDialog = () => {
    trackEvent({
      eventName: tempCulture
        ? EventNamesEnum.USER_CULTURE_VALIDATION_PAGE_CLICKED
        : EventNamesEnum.USER_CULTURE_SELECTION_PAGE_CLICKED,
      properties: {
        cta: "other culture",
        culture_position_shown: enabledCulturesShownPosition,
        default_culture_selection: tempCulture,
        page_name: DialogType.MAIN_CULTURES,
        culture_selected: "other culture",
      },
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        UseTrackingAnalyticsSendToEnum.FACEBOOK,
      ],
    });
    setCurrDialogType(DialogType.OTHER_CULTURES);
    setSelectedUserCulture(null);
  };

  const handleCTAClick = async () => {
    if (isLoading) return;
    if (!selectedUserCulture) {
      setSelectCultureError(
        t(
          "user_culture_screen.user_culture_selection.submitCta.disabled.tapError",
        ),
      );
      return;
    }

    const userCulture = availableCultures.find(
      (culture) => culture.abbreviation === selectedUserCulture,
    )!;

    const eventCta = "proceed_button";

    switch (currDialogType) {
      case DialogType.MAIN_CULTURES:
        await trackEvent({
          eventName: tempCulture
            ? EventNamesEnum.USER_CULTURE_VALIDATION_PAGE_CLICKED
            : EventNamesEnum.USER_CULTURE_SELECTION_PAGE_CLICKED,
          properties: {
            cta: eventCta,
            culture_position_shown: enabledCulturesShownPosition,
            default_culture_selection: tempCulture,
            page_name: DialogType.MAIN_CULTURES,
            culture_selected: userCulture.name.toLocaleLowerCase(),
          },
          sendTo: [
            UseTrackingAnalyticsSendToEnum.FIREBASE,
            UseTrackingAnalyticsSendToEnum.FACEBOOK,
          ],
        });

        handleCultureSave(userCulture, userCulture);
        break;
      case DialogType.OTHER_CULTURES:
        await trackEvent({
          eventName: EventNamesEnum.USER_CULTURE_SELECTION_PAGE_CLICKED,
          properties: {
            cta: eventCta,
            culture_position_shown: otherCulturesShownPosition,
            page_name: DialogType.OTHER_CULTURES,
            culture_selected: userCulture.name.toLocaleLowerCase(),
          },
          sendTo: [
            UseTrackingAnalyticsSendToEnum.FIREBASE,
            UseTrackingAnalyticsSendToEnum.FACEBOOK,
          ],
        });
        setCurrDialogType(DialogType.CONTENT_CULTURE);
        break;
      case DialogType.CONTENT_CULTURE:
        if (!selectedContentCulture) {
          setSelectCultureError(
            t(
              "user_culture_screen.user_culture_selection.submitCta.disabled.tapError",
            ),
          );
        }
        const contentCulture = enabledCultures.find(
          (culture) => culture.abbreviation === selectedContentCulture,
        )!;

        await trackEvent({
          eventName: EventNamesEnum.CONTENT_CULTURE_PAGE_CLICKED,
          properties: {
            cta: eventCta,
            culture_position_shown: enabledCulturesShownPosition,
            culture_selected: contentCulture.name.toLocaleLowerCase(),
          },
          sendTo: [
            UseTrackingAnalyticsSendToEnum.FIREBASE,
            UseTrackingAnalyticsSendToEnum.FACEBOOK,
          ],
        });
        handleCultureSave(userCulture, contentCulture);
    }
  };

  const handleCultureSave = async (
    userCulture: Culture,
    contentCulture: Culture,
  ) => {
    setIsLoading(true);
    const userCultureSelectionSource =
      currDialogType === DialogType.MAIN_CULTURES
        ? DialogType.MAIN_CULTURES
        : DialogType.OTHER_CULTURES;
    if (isLoggedIn) {
      const initialUserCulture = getClientSideCookieValue(
        cookiesValues[CookiesEnum.InitialUserCulture],
      );
      if (!initialUserCulture) {
        await trackEvent({
          eventName: EventNamesEnum.USER_CULTURE_RECEIVED,
          properties: {
            culture_received: userCulture.abbreviation,
            page_name: userCultureSelectionSource,
          },
          sendTo: [
            UseTrackingAnalyticsSendToEnum.FIREBASE,
            UseTrackingAnalyticsSendToEnum.FACEBOOK,
          ],
        });
      }
    } else {
      // so that we can trigger the recieved event after login
      localStorage.setItem(
        LocalStorageKeysEnum.USER_CULTURE_SELECTION_SOURCE,
        userCultureSelectionSource,
      );
    }

    await setUserAndContentCultureCookieAction({
      userCulture: userCulture,
      contentCulture: contentCulture,
    });

    let redirectTo = pathname.replace(
      cultureFromParams as string,
      contentCulture.name.toLocaleLowerCase(),
    );

    if (!redirectTo.includes(contentCulture.name.toLocaleLowerCase())) {
      redirectTo = `/${contentCulture.name.toLocaleLowerCase()}`;
    }

    // If the URL contains a param redirectionAfterLoginTo, redirect accordingly
    if (typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);
      const redirectionAfterLoginTo = searchParams.get(
        "redirectionAfterLoginTo",
      );

      if (redirectionAfterLoginTo) {
        const decodedRedirectionAfterLoginTo = atob(
          decodeURIComponent(redirectionAfterLoginTo ?? ""),
        );
        const redirectionParam = decodedRedirectionAfterLoginTo.replace(
          DialectsEnum.HARYANVI as string,
          contentCulture.name.toLocaleLowerCase(),
        );
        const finalRedirectUrl = await handlePostLoginRedirection(
          encodeURIComponent(btoa(redirectionParam)),
          login,
        );

        if (finalRedirectUrl) {
          redirectTo = finalRedirectUrl;
        }
      }
    }

    // Redirect if:
    //   - redirectTo is different than pathname
    //   - and the path does NOT end with /movie/* or /show/* (i.e., not /movie/anything or /show/anything)
    //   - but if the path ends with /movie or /show (no trailing segment), redirection is allowed
    const canRedirect =
      redirectTo !== pathname && !/\/(movie|show)\/[^/]+/i.test(pathname);

    if (canRedirect) {
      router.replace(redirectTo);
    } else {
      closeDialog();
    }
  };

  const getDialogContent = () => {
    switch (currDialogType) {
      case DialogType.OTHER_CULTURES:
        return (
          <OtherCulturesContent
            selectedUserCulture={selectedUserCulture}
            otherCulturesShownPosition={otherCulturesShownPosition}
            setSelectedUserCulture={setSelectedUserCulture}
            selectCultureError={selectCultureError}
            removeSelectCultureError={() => setSelectCultureError(null)}
          />
        );
      case DialogType.CONTENT_CULTURE:
        return (
          <ContentsCultureContent
            enabledCulturesShownPosition={enabledCulturesShownPosition}
            selectCultureError={selectCultureError}
            selectedContentCulture={selectedContentCulture}
            setSelectedContentCulture={setSelectedContentCulture}
          />
        );
      default:
        return (
          <MainCulturesContent
            selectedUserCulture={selectedUserCulture}
            selectCultureError={selectCultureError}
            defaultCultureSelection={tempCulture}
            enabledCulturesShownPosition={enabledCulturesShownPosition}
            setSelectedUserCulture={setSelectedUserCulture}
            openOtherCulturesDialog={openOtherCulturesDialog}
          />
        );
    }
  };

  const dialogContent = getDialogContent();
  const dialogTitle =
    currDialogType === DialogType.CONTENT_CULTURE
      ? t("user_culture_screen.content_culture.heading")
      : t("user_culture_screen.user_culture_selection.heading");

  const dialogDescription =
    currDialogType === DialogType.CONTENT_CULTURE
      ? t("user_culture_screen.content_culture.subHeading")
      : t("user_culture_screen.user_culture_selection.subHeading");

  const isCtaDisabled =
    currDialogType === DialogType.CONTENT_CULTURE
      ? !selectedContentCulture
      : !selectedUserCulture;

  const ctaTextKey =
    currDialogType === DialogType.CONTENT_CULTURE
      ? "content_culture"
      : "user_culture_selection";

  const ctaText = isLoading
    ? t("auth_screen.please_wait")
    : t(
        `user_culture_screen.${ctaTextKey}.submitCta.${isCtaDisabled ? "disabled" : "enabled"}.text`,
      );

  const isBackBtnVisible = currDialogType !== DialogType.MAIN_CULTURES;

  const handleBackBtnClick = () => {
    trackEvent({
      eventName: EventNamesEnum.USER_CULTURE_SELECTION_PAGE_CLICKED,
      properties: {
        cta: "back button",
        page_name: currDialogType,
      },
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        UseTrackingAnalyticsSendToEnum.FACEBOOK,
      ],
    });

    if (currDialogType === DialogType.CONTENT_CULTURE) {
      setSelectedContentCulture(null);
      setCurrDialogType(DialogType.OTHER_CULTURES);
    } else {
      setSelectedUserCulture(null);
      setCurrDialogType(DialogType.MAIN_CULTURES);
    }
  };

  if (isSkipPaywall || (onlyForLoggedInUser && !isLoggedIn)) {
    return null;
  }

  return (
    <Dialog open={isDialogOpen}>
      <DialogContent
        hideCloseButton
        preventCloseOnOutsideClick
        data-testid="user-culture-dialog"
        className="h-full w-full gap-0 md:h-fit md:w-fit"
      >
        <DialogHeader>
          <Column
            justify="end"
            fullHeight={currDialogType === DialogType.MAIN_CULTURES}
            gap={1}
          >
            <Row fullWidth justify="spaceBetween" align="center">
              <div className="h-8 w-8">
                <Logo wrapInLink={false} />
              </div>
              {isBackBtnVisible && (
                <Button noBg onClick={handleBackBtnClick}>
                  <Text
                    appearance="tertiary"
                    size={3}
                    weight="medium"
                    underline
                  >
                    {currDialogType === DialogType.OTHER_CULTURES
                      ? t("user_culture_screen.other_culture.goBackCta.text")
                      : t("user_culture_screen.content_culture.goBackCta.text")}
                  </Text>
                </Button>
              )}
            </Row>
            <h2 className="text-start text-3xl font-bold">{dialogTitle}</h2>
            <DialogDescription className="text-start">
              {dialogDescription}
            </DialogDescription>
          </Column>
        </DialogHeader>

        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
          {dialogContent}
        </div>

        <DialogFooter>
          <Button
            className="h-14 !rounded-lg"
            variant="primary-white"
            disabled={isCtaDisabled || isLoading}
            onClick={handleCTAClick}
            forceAllowClick={true}
            dataTestId="continue-button"
          >
            {ctaText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MainCulturesContent = ({
  selectedUserCulture,
  selectCultureError,
  enabledCulturesShownPosition,
  defaultCultureSelection,
  setSelectedUserCulture,
  openOtherCulturesDialog,
}: {
  selectedUserCulture: string | number | null;
  selectCultureError: string | null;
  defaultCultureSelection?: string;
  enabledCulturesShownPosition: string;
  setSelectedUserCulture: (culture: string | number) => void;
  openOtherCulturesDialog: () => void;
}) => {
  const { t } = useClientTranslations();
  const { trackEvent } = useTrackingAnalytics();
  const { enabledCultures } = useCulture();

  useEffect(() => {
    trackEvent({
      eventName: defaultCultureSelection
        ? EventNamesEnum.USER_CULTURE_VALIDATION_PAGE_VIEWED
        : EventNamesEnum.USER_CULTURE_SELECTION_PAGE_VIEWED,
      properties: {
        culture_position_shown: enabledCulturesShownPosition,
        default_culture_selection: defaultCultureSelection,
        page_name: DialogType.MAIN_CULTURES,
      },
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        UseTrackingAnalyticsSendToEnum.FACEBOOK,
      ],
    });
  }, []);

  const handleCultureSelection = (culture: Culture) => {
    setSelectedUserCulture(culture.abbreviation);
    trackEvent({
      eventName: defaultCultureSelection
        ? EventNamesEnum.USER_CULTURE_VALIDATION_PAGE_CLICKED
        : EventNamesEnum.USER_CULTURE_SELECTION_PAGE_CLICKED,
      properties: {
        cta: culture.name.toLocaleLowerCase(),
        culture_position_shown: enabledCulturesShownPosition,
        default_culture_selection: defaultCultureSelection,
        culture_selected: culture.name.toLocaleLowerCase(),
        page_name: DialogType.MAIN_CULTURES,
      },
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        UseTrackingAnalyticsSendToEnum.FACEBOOK,
      ],
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 py-4">
      <div className={selectCultureError ? "" : "invisible"}>
        <Text appearance="error" size={2}>
          {selectCultureError ||
            t(
              "user_culture_screen.user_culture_selection.submitCta.disabled.tapError",
            )}
        </Text>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto md:max-h-[28.125rem]">
        <div className="grid grid-cols-2 gap-4">
          {enabledCultures
            // sort to make the default culture selection (tempCulture) as the first culture option
            .sort((a, b) => {
              if (a.abbreviation === defaultCultureSelection) return -1;
              if (b.abbreviation === defaultCultureSelection) return 1;
              return 0;
            })
            .map((culture) => (
              <CultureCard
                key={culture.abbreviation}
                culture={culture}
                isSelected={selectedUserCulture === culture.abbreviation}
                onSelect={() => handleCultureSelection(culture)}
              />
            ))}
        </div>
      </div>
      <button
        className="w-full flex-shrink-0"
        onClick={openOtherCulturesDialog}
        data-testid="culture-option"
      >
        <Box
          width="w-full"
          paddingVertical={2}
          paddingHorizontal={4}
          background="newSecondary"
          borderRadius={2}
          borderColor="newPrimary"
          borderWidth={2}
        >
          <Row fullWidth justify="spaceBetween">
            <Text size={5} weight="medium">
              {t(
                "user_culture_screen.other_culture.optionsTitle.deselected.text",
              )}
            </Text>
            <ChevronRight className="h-7 w-7" />
          </Row>
        </Box>
      </button>
    </div>
  );
};

const OtherCulturesContent = ({
  selectedUserCulture,
  selectCultureError,
  otherCulturesShownPosition,
  setSelectedUserCulture,
  removeSelectCultureError,
}: {
  selectedUserCulture: string | number | null;
  selectCultureError: string | null;
  otherCulturesShownPosition: string;
  setSelectedUserCulture: (culture: string | number) => void;
  removeSelectCultureError: () => void;
}) => {
  const { otherCultures } = useCulture();
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useClientTranslations();
  const { trackEvent } = useTrackingAnalytics();

  const filteredCultures = otherCultures.filter((culture) =>
    culture.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const searchNoResultFound = filteredCultures.length === 0;

  useEffect(() => {
    if (searchQuery.length > 0 && searchQuery.length % 2 === 0) {
      trackEvent({
        eventName: EventNamesEnum.CULTURE_SEARCH_INITIATED,
        properties: {
          stage: SearchStageEnum.TYPING,
          search_keyword: searchQuery,
          is_search_result_showed: !searchNoResultFound,
        },
        sendTo: [
          UseTrackingAnalyticsSendToEnum.FIREBASE,
          UseTrackingAnalyticsSendToEnum.FACEBOOK,
        ],
      });
    }
  }, [searchQuery]);

  useEffect(() => {
    if (selectCultureError) {
      removeSelectCultureError();
    }
  }, [searchQuery]);

  useEffect(() => {
    trackEvent({
      eventName: EventNamesEnum.USER_CULTURE_SELECTION_PAGE_VIEWED,
      properties: {
        culture_position_shown: otherCulturesShownPosition,
        page_name: DialogType.OTHER_CULTURES,
      },
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        UseTrackingAnalyticsSendToEnum.FACEBOOK,
      ],
    });
  }, []);

  const handleCultureSelection = (culture: Culture) => {
    setSelectedUserCulture(culture.abbreviation);

    trackEvent({
      eventName: EventNamesEnum.USER_CULTURE_SELECTION_PAGE_CLICKED,
      properties: {
        cta: culture.name.toLocaleLowerCase(),
        culture_position_shown: otherCulturesShownPosition,
        culture_selected: culture.name.toLocaleLowerCase(),
        page_name: DialogType.OTHER_CULTURES,
      },
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        UseTrackingAnalyticsSendToEnum.FACEBOOK,
      ],
    });
  };

  const handleSearchInputFocus = () => {
    trackEvent({
      eventName: EventNamesEnum.CULTURE_SEARCH_INITIATED,
      properties: {
        stage: SearchStageEnum.TAP,
      },
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        UseTrackingAnalyticsSendToEnum.FACEBOOK,
      ],
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 py-4">
      <div className="flex-shrink-0 space-y-1">
        <div className="relative h-10 w-full">
          <Input
            type="text"
            placeholder={t(
              "user_culture_screen.other_culture.searchPlaceHolder",
            )}
            onFocus={handleSearchInputFocus}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            suffixIcon={<SearchIcon fillColor="stg-grey-4" />}
          />
        </div>
        <div
          className={
            searchNoResultFound || selectCultureError ? "" : "invisible"
          }
        >
          <Text appearance="error" size={2}>
            {selectCultureError ||
              t("user_culture_screen.other_culture.noSearchResultFound")}
          </Text>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto md:max-h-[26.5625rem]">
        {(filteredCultures.length ? filteredCultures : otherCultures).map(
          (culture) => (
            <CultureOption
              key={culture.abbreviation}
              icon={""}
              text={culture.screens?.selectionScreen?.title || ""}
              isSelected={selectedUserCulture === culture.abbreviation}
              onSelect={() => handleCultureSelection(culture)}
            />
          ),
        )}
      </div>
    </div>
  );
};

const CultureOption = ({
  icon,
  text,
  isSelected,
  onSelect,
}: {
  icon?: string;
  text: string;
  isSelected: boolean;
  onSelect: () => void;
}) => (
  <button onClick={onSelect} className="w-full" data-testid="culture-option">
    <Box
      borderRadius={3}
      background="newSecondary"
      width="w-full"
      height={icon ? "h-24" : "h-16"}
      borderColor={isSelected ? "newTertiary" : "newPrimary"}
      borderWidth={3}
      overflow="hidden"
      dataTestId={isSelected ? "selected-culture-option" : undefined}
    >
      <Row fullWidth gap={3} fullHeight>
        {icon && (
          <Box height="h-full" aspectRatio="square" position="relative">
            <Img
              src={icon}
              alt={text}
              className="aspect-square h-full w-full object-cover"
              fill
            />
          </Box>
        )}

        <Box fillInFlex paddingHorizontal={4} height="h-full">
          <Row fullHeight justify="spaceBetween" align="center">
            <Text size={5} weight="bold">
              {text}
            </Text>
            <div
              className={`${
                isSelected ? "border-2 border-stg-green" : "bg-stg-grey-3"
              } flex h-5 w-5 items-center justify-center rounded-full`}
            >
              {isSelected && (
                <div className="h-2 w-2 rounded-full bg-stg-green"></div>
              )}
            </div>
          </Row>
        </Box>
      </Row>
    </Box>
  </button>
);

/** CultureCard component for 2-column grid layout in MainCulturesContent */
const CultureCard = ({
  culture,
  isSelected,
  onSelect,
}: {
  culture: Culture;
  isSelected: boolean;
  onSelect: () => void;
}) => (
  <button onClick={onSelect} className="w-full" data-testid="culture-option">
    <div
      className={`flex flex-col items-center gap-2 rounded-xl p-1 `}
      data-testid={isSelected ? "selected-culture-option" : undefined}
    >
      <Img
        src={`${process.env.NEXT_PUBLIC_CDN_ROOT_URL}${culture.imageUrl}`}
        alt={culture.name}
        className={`aspect-square h-36 w-36 rounded-2xl object-cover p-0.5 ${isSelected ? "border border-white" : "border border-transparent"}`}
        width={142}
        height={142}
      />
      <Text size={5}>{culture.title}</Text>
    </div>
  </button>
);

const ContentsCultureContent = ({
  selectedContentCulture,
  selectCultureError,
  enabledCulturesShownPosition,
  setSelectedContentCulture,
}: {
  selectedContentCulture: string | number | null;
  enabledCulturesShownPosition: string;
  selectCultureError: string | null;
  setSelectedContentCulture: (culture: string | number) => void;
}) => {
  const { t } = useClientTranslations();
  const { enabledCultures } = useCulture();
  const { trackEvent } = useTrackingAnalytics();

  useEffect(() => {
    trackEvent({
      eventName: EventNamesEnum.CONTENT_CULTURE_PAGE_VIEWED,
      properties: {
        culture_position_shown: enabledCulturesShownPosition,
      },
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        UseTrackingAnalyticsSendToEnum.FACEBOOK,
      ],
    });
  }, []);

  const handleCultureSelection = (culture: Culture) => {
    setSelectedContentCulture(culture.abbreviation);

    trackEvent({
      eventName: EventNamesEnum.CONTENT_CULTURE_PAGE_CLICKED,
      properties: {
        culture_position_shown: enabledCulturesShownPosition,
        culture_selected: culture.name.toLocaleLowerCase(),
        cta: culture.name.toLocaleLowerCase(),
      },
      sendTo: [
        UseTrackingAnalyticsSendToEnum.FIREBASE,
        UseTrackingAnalyticsSendToEnum.FACEBOOK,
      ],
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 py-4">
      <div className={selectCultureError ? "" : "invisible"}>
        <Text appearance="error" size={2}>
          {selectCultureError ||
            t("popups.userCulture.selectCultureToContinueError")}
        </Text>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto md:max-h-[425px]">
        <div className="grid grid-cols-2 gap-4">
          {enabledCultures.map((culture) => (
            <CultureCard
              key={culture.abbreviation}
              culture={culture}
              isSelected={selectedContentCulture === culture.abbreviation}
              onSelect={() => handleCultureSelection(culture)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
